import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../../API/axiosConfig';
import { socialMediaAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';
import { combineCustomAndRegularBatchDataCodes, findLatestExistingBatchData, batchDataSort } from '../../../helperFunctions';
import ChartCalculations from '../SocialMedia/chartCalculations';
import ReportLayout from '../../../myComponents/report/ReportLayout';
import { defaultFilterState, formatFilterValue, formatLabelValue } from '../../../myComponents/filters/helpers';

const parseQueryJSON = key => {
  try {
    const raw = new URLSearchParams(window.location.search).get(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return JSON.parse(decodeURIComponent(raw));
    }
  } catch (error) {
    return null;
  }
};

const normalizeTargetMarketFilters = ({ exportFilters, batchCountry }) => {
  const allowedFilterKeys = [
    'Age',
    'Gender',
    'Homeownership',
    'Ethnicity',
    'Origin',
    'Education',
    'Income',
    'State',
    'Location',
    'PersonalIdeology_1',
    'Marital_status',
  ];

  const filterSource = exportFilters && typeof exportFilters === 'object' ? exportFilters : null;
  const normalized = { ...defaultFilterState(), ...(filterSource || {}) };

  const lines = [];
  allowedFilterKeys.forEach(key => {
    const values = Array.isArray(normalized[key])
      ? normalized[key].filter(v => v !== null && typeof v !== 'undefined' && `${v}` !== '')
      : [];
    values.forEach(value => {
      const mappedLabel = formatLabelValue(key);
      const mappedValue = formatFilterValue(key, value, batchCountry);
      lines.push(`${mappedLabel}: ${mappedValue || value}`);
    });
  });

  return lines.length ? [...new Set(lines)] : ['All consumers'];
};

const resolveBatchInfo = (combinedBatchDataCodes, requestedCountry, requestedBatchData) => {
  const availableBatchCountries = Object.keys(combinedBatchDataCodes || {});
  const resolvedBatchCountry = combinedBatchDataCodes?.[requestedCountry]
    ? requestedCountry
    : combinedBatchDataCodes?.[DEFAULT_BATCH_COUNTRY]
    ? DEFAULT_BATCH_COUNTRY
    : availableBatchCountries[0];

  if (!resolvedBatchCountry || !combinedBatchDataCodes?.[resolvedBatchCountry]) {
    throw new Error('No batch data available for export.');
  }

  const countryBatches = combinedBatchDataCodes[resolvedBatchCountry];
  const requestedYear = requestedBatchData?.year;
  const resolvedYear = countryBatches?.[requestedYear]
    ? requestedYear
    : findLatestExistingBatchData(countryBatches).latestYear;

  const availableQuarters = (countryBatches?.[resolvedYear] || []).sort(batchDataSort);
  const requestedQuarters = Array.isArray(requestedBatchData?.quarter) ? requestedBatchData.quarter : [];
  const resolvedQuarters = requestedQuarters.filter(quarter => availableQuarters.includes(quarter));

  return {
    batchCountry: resolvedBatchCountry,
    batchData: {
      year: resolvedYear,
      quarter: resolvedQuarters.length ? resolvedQuarters : [availableQuarters[availableQuarters.length - 1]],
    },
  };
};

const MarketingStrategyReport = () => {
  const { id } = useParams();
  const [state, setState] = useState({ ready: false, persona: null, post: null, result: null, meta: [], error: '' });

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get('/fs/data');
        const appData = data.result;
        const normalizedAppData = {
          ...appData,
          combinedBatchDataCodes:
            appData?.combinedBatchDataCodes ||
            combineCustomAndRegularBatchDataCodes(appData?.existingBatchData || {}, appData?.customBatchData || {}),
          socialMediaPosts: appData?.socialMediaPosts || appData?.socialPosts || [],
        };
        const post = (normalizedAppData.socialMediaPosts || []).find(e => e.id === id);
        if (!post) throw new Error('Marketing strategy post not found for export.');

        const exportFilters = parseQueryJSON('exportFilters');
        const exportContext = parseQueryJSON('exportContext');

        const requestedPersonaId = exportContext?.brandPersonaId;
        const persona =
          (normalizedAppData.brandPersonas || []).find(e => e.id == requestedPersonaId) ||
          (normalizedAppData.brandPersonas || []).find(e => e.id == post?.Post_Brand_Persona);
        if (!persona) throw new Error('Associated brand persona not found for selected post.');

        const { batchCountry, batchData } = resolveBatchInfo(
          normalizedAppData?.combinedBatchDataCodes,
          exportContext?.batchCountry,
          exportContext?.batchData,
        );

        const filtersForCalculation =
          exportContext?.filters && typeof exportContext.filters === 'object' ? exportContext.filters : null;

        const result = await socialMediaAlgorithmCalculation(
          { filters: filtersForCalculation, socialPost: post, persona, batches: batchData, batchCountry },
          () => {},
          [],
        );
        const exportSafePost = { ...post, Post_Link: '' };

        const generatedAt = new Date();
        const generatedDate = generatedAt.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
        const generatedTime = generatedAt.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        });

        const targetMarketFilters = normalizeTargetMarketFilters({
          exportFilters: exportFilters || filtersForCalculation,
          batchCountry,
        });

        setState({
          ready: true,
          persona,
          post: exportSafePost,
          result,
          error: '',
          meta: [
            { label: 'Campaign', value: post?.Post_Title || 'N/A' },
            { label: 'Brand', value: persona?.Name || 'N/A' },
            { label: 'Country', value: batchCountry || 'N/A' },
            { label: 'Year Number', value: batchData.year || 'N/A' },
            { label: 'Quarter', value: batchData.quarter?.join(', ') || 'N/A' },
            { label: 'Generated Date', value: generatedDate || 'N/A' },
            { label: 'Generated Time', value: generatedTime || 'N/A' },
            ...targetMarketFilters.map(value => ({ label: 'Target Market', value: value || 'N/A' })),
          ],
        });
      } catch (error) {
        const fallbackTargetMarketFilters = ['All consumers'];
        setState({
          ready: true,
          persona: null,
          post: null,
          result: null,
          error: error?.message || 'Failed to prepare report data.',
          meta: [
            { label: 'Campaign', value: 'N/A' },
            { label: 'Brand', value: 'N/A' },
            { label: 'Country', value: 'N/A' },
            { label: 'Year Number', value: 'N/A' },
            { label: 'Quarter', value: 'N/A' },
            { label: 'Generated Date', value: 'N/A' },
            { label: 'Generated Time', value: 'N/A' },
            ...fallbackTargetMarketFilters.map(value => ({ label: 'Target Market', value: value || 'N/A' })),
          ],
        });
      }
    };
    run();
  }, [id]);

  return (
    <ReportLayout
      title="Marketing Strategy Report"
      metadata={state.meta}
      ready={state.ready}
      hasError={!!state.error}
      errorMessage={state.error}
      coverType="marketing-strategy"
      moodBoardExport={{
        moodBoard: state.post?.moodBoards?.default || null,
        campaignTitle: state.post?.Post_Title || '',
        pillarTitles: [state.post?.Post_Keyword_1, state.post?.Post_Keyword_2, state.post?.Post_Keyword_3],
      }}>
      {!state.result && state.error && <p>{state.error}</p>}
      {state.result && (
        <ChartCalculations
          data={state.result}
          persona={state.persona}
          socialPost={state.post}
          AIStrategyGenerator={{ loading: false, text: state.post?.marketingStrategies?.[state.persona?.id] || '' }}
          setAIStrategyGenerator={() => {}}
          visibleAgencies={[]}
        />
      )}
    </ReportLayout>
  );
};

export default MarketingStrategyReport;
